import React, { forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import FormFeedback from './FormFeedback';
import './FormInput.scss';

const FormInput = forwardRef(({
  id,
  name,
  type = 'text',
  initialValue,
  validate,
  maxLength,
  labelRenderer,
  disabled = false,
  measurement,
  step,
  formatOnBlur,
  format,
  hidden = false,
  children,
  onClick,
  givenRef 
}, ref) => {
  const inputId = id || `form-input--${name}`;
  const localRef = useRef(null);
  const activeRef = ref || givenRef || localRef;

  return (
    <label
      htmlFor={inputId}
      className={classNames('form-input', {
        'form-input--disabled': disabled,
        'form-input--hidden': hidden
      })}
    >
      {typeof labelRenderer === 'function' && labelRenderer()}
      
      <Field
        name={name}
        type={type}
        initialValue={initialValue}
        validate={validate}
        format={format}
        formatOnBlur={formatOnBlur}
      >
        {({ input, meta }) => {
          const hasError = (meta.error && meta.touched) || 
                          (meta.submitError && !meta.dirtySinceLastSubmit);

          return (
            <div className={classNames({ 'measurement': measurement })}>
              <input
                {...input}
                value={input.value !== undefined && input.value !== null ? input.value : ''} 
                id={inputId}
                ref={activeRef}
                type={type}
                disabled={disabled}
                maxLength={maxLength}
                step={step}
                onClick={onClick}
                onChange={(e) => input.onChange(e.target.value)}
                onFocus={(e) => {
                  input.onFocus(e);
                  if (activeRef.current) activeRef.current.select();
                }}
                className={classNames({ 'form-input__control': !hidden })}
                size={measurement ? maxLength : null}
              />
              
              {children && <span>{children}</span>}
              {measurement && <span>{measurement}</span>}

              {hasError && (
                <FormFeedback positive={false}>
                  {meta.error || meta.submitError}
                </FormFeedback>
              )}
            </div>
          );
        }}
      </Field>
    </label>
  );
});

FormInput.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['email', 'password', 'text', 'date', 'time', 'number']),
  initialValue: PropTypes.any,
  validate: PropTypes.func,
  maxLength: PropTypes.number,
  labelRenderer: PropTypes.func,
  disabled: PropTypes.bool,
  measurement: PropTypes.string,
  step: PropTypes.string,
  formatOnBlur: PropTypes.bool,
  format: PropTypes.func,
  hidden: PropTypes.bool,
  children: PropTypes.node,
  onClick: PropTypes.func,
  givenRef: PropTypes.any
};

export default FormInput;
